import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiAlertTriangle, FiCheckCircle, FiInfo, FiLoader, FiX } from "react-icons/fi";

export type ToastKind = "success" | "error" | "info" | "warning" | "loading";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastContent {
  title: string;
  description?: string;
  action?: ToastAction;
}

export interface ToastOptions {
  duration?: number; // ms
  persistent?: boolean;
}

export interface ToastPromiseMessages<T> {
  loading: string | ToastContent;
  success: string | ToastContent | ((value: T) => string | ToastContent);
  error: string | ToastContent | ((error: unknown) => string | ToastContent);
}

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  action?: ToastAction;
  kind: ToastKind;
  createdAt: number;
  duration: number;
  persistent: boolean;
}

interface ToastContextValue {
  show: (
    message: string | ToastContent,
    kind?: ToastKind,
    options?: ToastOptions
  ) => string;
  success: (message: string | ToastContent, options?: ToastOptions) => string;
  error: (message: string | ToastContent, options?: ToastOptions) => string;
  info: (message: string | ToastContent, options?: ToastOptions) => string;
  warning: (message: string | ToastContent, options?: ToastOptions) => string;
  loading: (message: string | ToastContent, options?: ToastOptions) => string;
  update: (
    id: string,
    message: string | ToastContent,
    kind?: ToastKind,
    options?: ToastOptions
  ) => void;
  dismiss: (id: string) => void;
  promise: <T>(promise: Promise<T>, messages: ToastPromiseMessages<T>) => Promise<T>;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  useEffect(
    () => () => {
      timers.current.forEach((timer) => clearTimeout(timer));
      timers.current.clear();
    },
    []
  );

  const show = useCallback(
    (
      message: string | ToastContent,
      kind: ToastKind = "info",
      options?: ToastOptions
    ) => {
      const id = Math.random().toString(36).slice(2);
      const duration = options?.duration ?? (kind === "loading" ? 0 : 4000);
      const content =
        typeof message === "string"
          ? { title: message }
          : {
              title: message.title,
              description: message.description,
              action: message.action,
            };
      const persistent = options?.persistent ?? kind === "loading";
      const toast: ToastItem = {
        id,
        ...content,
        kind,
        createdAt: Date.now(),
        duration,
        persistent,
      };
      setToasts((list) => [...list, toast]);
      if (!persistent) {
        timers.current.set(
          id,
          setTimeout(() => remove(id), duration)
        );
      }
      return id;
    },
    [remove]
  );

  const update = useCallback(
    (
      id: string,
      message: string | ToastContent,
      kind: ToastKind = "info",
      options?: ToastOptions
    ) => {
      const timer = timers.current.get(id);
      if (timer) clearTimeout(timer);
      timers.current.delete(id);

      const duration = options?.duration ?? (kind === "loading" ? 0 : 4000);
      const persistent = options?.persistent ?? kind === "loading";
      const content =
        typeof message === "string"
          ? { title: message }
          : {
              title: message.title,
              description: message.description,
              action: message.action,
            };

      setToasts((list) =>
        list.map((toast) =>
          toast.id === id
            ? { ...toast, ...content, kind, duration, persistent, createdAt: Date.now() }
            : toast
        )
      );

      if (!persistent) {
        timers.current.set(
          id,
          setTimeout(() => remove(id), duration)
        );
      }
    },
    [remove]
  );

  const api = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m, o) => show(m, "success", o),
      error: (m, o) => show(m, "error", o),
      info: (m, o) => show(m, "info", o),
      warning: (m, o) => show(m, "warning", o),
      loading: (m, o) => show(m, "loading", { ...o, persistent: true }),
      update,
      dismiss: remove,
      promise: async (promise, messages) => {
        const id = show(messages.loading, "loading", { persistent: true });
        try {
          const value = await promise;
          update(
            id,
            typeof messages.success === "function"
              ? messages.success(value)
              : messages.success,
            "success"
          );
          return value;
        } catch (error) {
          update(
            id,
            typeof messages.error === "function" ? messages.error(error) : messages.error,
            "error"
          );
          throw error;
        }
      },
    }),
    [remove, show, update]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] z-50 flex flex-col items-center gap-2 px-3 pointer-events-none sm:bottom-5 sm:px-4"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.kind === "error" ? "alert" : "status"}
            aria-live={t.kind === "error" ? "assertive" : "polite"}
            className={[
              "max-w-md w-full rounded-2xl border shadow-lg px-4 py-3 flex items-start gap-3 pointer-events-auto",
              t.kind === "success" && "border-accent-200 bg-accent-50 text-accent-800",
              t.kind === "error" && "border-rose-200 bg-rose-50 text-rose-800",
              t.kind === "info" && "border-sky-200 bg-sky-50 text-sky-800",
              t.kind === "warning" && "border-amber-200 bg-amber-50 text-amber-800",
              t.kind === "loading" && "border-primary-200 bg-primary-50 text-primary-800",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span className="mt-0.5">
              {t.kind === "success" && <FiCheckCircle />}
              {t.kind === "error" && <FiAlertTriangle />}
              {t.kind === "info" && <FiInfo />}
              {t.kind === "warning" && <FiAlertTriangle />}
              {t.kind === "loading" && <FiLoader className="animate-spin" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-5">{t.title}</div>
              {t.description ? (
                <div className="mt-0.5 text-xs font-medium leading-5 opacity-85">
                  {t.description}
                </div>
              ) : null}
              {t.action ? (
                <button
                  type="button"
                  onClick={() => {
                    t.action?.onClick();
                    remove(t.id);
                  }}
                  className="mt-2 inline-flex h-8 items-center justify-center rounded-lg border border-current/20 bg-white/55 px-3 text-xs font-bold transition hover:bg-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-current/30"
                >
                  {t.action.label}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg opacity-70 transition hover:bg-white/60 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current/30"
              aria-label="Fechar aviso"
            >
              <FiX />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}

export function useOptionalToast() {
  return useContext(ToastContext);
}
/**
 * Mantém o feedback contextual da tela e o torna imediatamente perceptível no
 * canal global. É intencionalmente compatível com o retorno de `useState` para
 * que formulários legados não precisem duplicar cada `setError` com um toast.
 */
export function useFeedbackState<T extends string | null | undefined>(
  initialState: T,
  kind: Extract<ToastKind, "error" | "success" | "info" | "warning"> = "error"
) {
  const [{ message, sequence }, setFeedback] = useState({
    message: initialState,
    sequence: 0,
  });
  const toast = useOptionalToast();

  const setMessage = useCallback((action: React.SetStateAction<T>) => {
    setFeedback((current) => ({
      message:
        typeof action === "function"
          ? (action as (previous: T) => T)(current.message)
          : action,
      sequence: current.sequence + 1,
    }));
  }, []);

  useEffect(() => {
    if (message) {
      toast?.[kind](message);
    }
  }, [kind, message, sequence, toast]);

  return [message, setMessage] as const;
}
